import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Loader } from "lucide-react";
import { QUOTE_STATUS_DISPLAY_NAMES } from "@forgepoint/shared";
import { useRecordQuotesStore } from "@/modules/quotes/quotesStore";
import { DataTableBadge } from "@/components/ui/data-table-badge";

/** Max associated quotes fetched for the list. */
const ASSOCIATED_QUOTES_LIMIT = 50;

/** Props for {@link ItemAssociatedQuotes}. */
interface ItemAssociatedQuotesProps {
  /** The item whose referencing quotes are listed. */
  itemId: string;
}

/**
 * Lists the non-archived quotes that reference an item via a quote line,
 * newest first. Results are scoped by the caller's quote-view permissions.
 * Clicking a quote navigates to the quotes page and opens that quote.
 */
export function ItemAssociatedQuotes({ itemId }: ItemAssociatedQuotesProps) {
  const navigate = useNavigate();
  const { quotes, fetchQuotes } = useRecordQuotesStore();
  const [quoteIds, setQuoteIds] = useState<string[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchQuotes({ itemIds: [itemId], limit: ASSOCIATED_QUOTES_LIMIT }).then(
      (ids) => {
        if (!cancelled) {
          setQuoteIds(ids);
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [itemId, fetchQuotes]);

  const associatedQuotes = (quoteIds ?? [])
    .map((id) => quotes.get(id))
    .filter((quote) => quote !== undefined);

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium">Associated Quotes</h3>
      {quoteIds === null ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader className="h-4 w-4 animate-spin" />
          Loading quotes
        </div>
      ) : associatedQuotes.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          This item is not used in any quotes.
        </p>
      ) : (
        <div className="max-h-64 overflow-y-auto rounded-md border divide-y">
          {associatedQuotes.map((quote) => (
            <button
              key={quote.id}
              type="button"
              onClick={() =>
                navigate("/quotes", { state: { openQuoteId: quote.id } })
              }
              className="flex w-full items-center justify-between gap-2 px-3 py-2 text-sm text-left hover:bg-muted/50 transition-colors"
            >
              <div className="min-w-0">
                <span className="font-medium">#{quote.quoteDisplayNumber}</span>
                {quote.name && (
                  <span className="text-muted-foreground truncate">
                    {" "}
                    — {quote.name}
                  </span>
                )}
              </div>
              <DataTableBadge
                color={quote.status === "FINALIZED" ? "green" : "slate"}
              >
                {QUOTE_STATUS_DISPLAY_NAMES[quote.status]}
              </DataTableBadge>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
